import { FlexibleText } from '@mentimeter/slides-presentation-ui/text/flexible-text';
import { animated } from '@react-spring/web';
import { useMentiSpring } from '@mentimeter/motion/spring';
import React from 'react';
import { ThemeProvider } from '@mentimeter/ragnar-react';
import { Label } from '@mentimeter/slides-presentation-ui/title/label';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import { MarkdownText } from '@mentimeter/slides-presentation-ui/text/markdown-text';
import type { NumberStaticContent } from '@core-api/presentation/types/response';
import {
  usePresentationContext,
  usePresentationSlideContext,
} from '../../legacy-context/presentationContext';
import { getNinePointLayoutProperties } from '../../shared/utils/getNinePointLayoutProperties';
import { StaggerCss } from '../../shared/components/StaggerCss';
import { getLegacyLayout } from '../../utils/legacyContentSlideUtils/getLegacyLayout';

const MAX_LENGTH = 9;
const BREAKPOINTS = [
  { count: 12, offset: -8 },
  { count: 7, offset: -6 },
  { count: 4, offset: -3 },
];
const DEFAULT_SIZE_INDEX = 10;
// Mega size array (padded for variable font size (-2 +2) -> (--S--M--L--))
const FONT_SIZES_THEME = {
  fontSizes: [100, 130, 140, 210, 230, 260, 300, 350, 410, 470, 530, 550, 570],
};

export function NumberPresentation() {
  const { useConfig } = usePresentationContext();
  const slide = usePresentationSlideContext();
  const staticContent = slide.staticContent as NumberStaticContent;
  const fontSizeOffset = staticContent.textSizeOffset;
  const legacyLayout = getLegacyLayout(slide) ?? {
    type: 'default',
    font_size_offset: 0,
    alignment_x: 1,
    alignment_y: 1,
    size: '25%',
  };

  const { mode, enableAnimations } = useConfig();
  const displayString = staticContent.number.substring(0, MAX_LENGTH);

  // Determine if displayString has a decimal part
  const isIntegerAnimation = /^\d+$/.test(displayString);

  // Numeric target for animation
  const targetNumber = parseFloat(parseFloat(displayString).toFixed(1));

  // Formatted display number
  const displayNumber = targetNumber.toFixed(isIntegerAnimation ? 0 : 1);

  const numberLength = displayNumber.replace('.', '').length;
  const [length, setLength] = React.useState(numberLength);

  const [animatedValue] = useMentiSpring({
    from: { t: 0 },
    to: { t: targetNumber },
    config: {
      mass: 2,
      tension: 300,
      friction: 55,
      clamp: true,
      velocity: 0.1,
      precision: isIntegerAnimation ? 1 : 0.1,
    },
    onChange: ({ value }) => {
      setLength((length) => {
        const currentLength = isIntegerAnimation
          ? Math.floor(value.t).toString().length
          : value.t.toFixed(1).replace('.', '').length;
        // only continuously update length if we're animating to a shorter number
        return currentLength !== length && numberLength !== length
          ? Math.max(numberLength, currentLength)
          : length;
      });
    },
  });

  // If layout takes space, make the font size smaller
  const layoutType = legacyLayout?.type || 'default';
  const offset =
    fontOffset({
      width: length,
      height: layoutType === 'default' || layoutType === 'full-bg' ? 1 : 2,
    }) + (fontSizeOffset ?? 0);

  const { textAlign, justifyContent, alignItems } =
    getNinePointLayoutProperties(legacyLayout);

  return (
    <Box
      width="100%"
      height="100%"
      data-testid="number-container"
      alignItems={alignItems}
      justifyContent={justifyContent}
    >
      <StaggerCss index={1} height="auto" enableAnimations={enableAnimations}>
        <ThemeProvider theme={FONT_SIZES_THEME}>
          <FlexibleText
            as="h1"
            aria-label={staticContent.number}
            fontWeight="semiBold"
            color="text"
            lineHeight="none"
            fontFamily="heading"
            fontOffset={offset}
            textAlign={textAlign}
            fontSize={DEFAULT_SIZE_INDEX}
            mb={staticContent.what || staticContent.label ? 3 : 0}
          >
            {mode === 'screenshot' ? (
              displayNumber
            ) : (
              <animated.div>
                {animatedValue.t.to((n) =>
                  n
                    .toFixed(isIntegerAnimation ? 0 : 1)
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ' '),
                )}
              </animated.div>
            )}
          </FlexibleText>
        </ThemeProvider>
        {staticContent.label && (
          <Label>
            <MarkdownText
              source={staticContent.label}
              disableLineBreaks
              allowHyperlinks
            />
          </Label>
        )}
      </StaggerCss>
      {staticContent.what && (
        <StaggerCss index={2} height="auto" enableAnimations={enableAnimations}>
          <Text
            color="text"
            fontSize={3}
            mb="-50px"
            mt={1}
            textAlign={textAlign}
          >
            <MarkdownText
              source={staticContent.what}
              disableLineBreaks={false}
              allowBlankLines
              allowHyperlinks
            />
          </Text>
        </StaggerCss>
      )}
    </Box>
  );
}

interface FontOffsetT {
  height: number;
  width: number;
}

const fontOffset = ({ width, height }: FontOffsetT): number => {
  // Could be thought of an area or simply the length of the string
  const approximatedCount = height * width;

  // Categorize which offset this element belongs to
  const { offset = 0 } =
    BREAKPOINTS.find(({ count }) => approximatedCount >= count) ?? {};
  return offset;
};
